import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Anchor, Map } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useReveal } from '@/hooks/useReveal';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { BASE_URL, CX_SERVICE_SLUGS, cxIcon } from './cxData';

const display = { fontFamily: 'var(--f-display)', color: 'var(--ink)' } as const;
const mono = { fontFamily: 'var(--f-mono)' } as const;

const Tag = ({ children, light = false }: { children: string; light?: boolean }) => (
  <div className="text-xs font-bold uppercase tracking-[0.18em] mb-4" style={{ ...mono, color: light ? 'var(--amber)' : 'var(--blue)' }}>
    {children}
  </div>
);

const H2 = ({ children, light = false, className = '' }: { children: string; light?: boolean; className?: string }) => (
  <h2 className={`text-3xl md:text-4xl font-bold leading-tight mb-6 ${className}`} style={{ fontFamily: 'var(--f-display)', color: light ? 'var(--white)' : 'var(--ink)' }}>
    {children}
  </h2>
);

const IconCircle = ({ icon, dark = false }: { icon: string; dark?: boolean }) => {
  const Icon = cxIcon(icon);
  return (
    <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: dark ? 'var(--ink)' : 'var(--blue)', color: 'var(--white)' }}>
      <Icon size={20} />
    </div>
  );
};

const CxHubPage = () => {
  const { t, language } = useTranslation();
  const cx = t.cx;
  useReveal();

  usePageMeta({ title: cx.meta.hubTitle, description: cx.meta.hubDescription, canonical: `${BASE_URL}/cx` });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${BASE_URL}/cx#page`,
        url: `${BASE_URL}/cx`,
        name: cx.meta.hubTitle,
        description: cx.meta.hubDescription,
        inLanguage: language,
        isPartOf: { '@id': `${BASE_URL}/#website` },
        about: { '@id': `${BASE_URL}/#organization` },
      },
      ...cx.services.items.map((s) => ({
        '@type': 'Service',
        '@id': `${BASE_URL}/cx/services/${s.slug}#service`,
        serviceType: s.title,
        description: s.summary,
        url: `${BASE_URL}/cx/services/${s.slug}`,
        provider: { '@id': `${BASE_URL}/#organization` },
        areaServed: [
          { '@type': 'Country', name: 'Germany' },
          { '@type': 'Country', name: 'Austria' },
          { '@type': 'Country', name: 'Switzerland' },
          { '@type': 'Place', name: 'European Union' },
        ],
        availableLanguage: ['de', 'en', 'ro'],
      })),
      {
        '@type': 'Person',
        '@id': `${BASE_URL}/#daniel-dobos`,
        name: cx.leadership.name,
        jobTitle: 'Founder & Managing Director',
        worksFor: { '@id': `${BASE_URL}/#organization` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: cx.breadcrumbs.home, item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: cx.breadcrumbs.cx, item: `${BASE_URL}/cx` },
        ],
      },
    ],
  };

  return (
    <>
      <CustomCursor />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <main style={{ paddingTop: 80 }}>
        {/* Hero */}
        <section style={{ background: 'var(--blue)', color: 'var(--white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
            <div className="text-xs font-bold uppercase tracking-[0.18em] mb-6" style={{ ...mono, color: 'var(--amber)' }}>{cx.hero.eyebrow}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] max-w-4xl mb-8" style={{ fontFamily: 'var(--f-display)' }}>{cx.hero.title}</h1>
            <p className="text-lg md:text-xl leading-relaxed max-w-3xl mb-10" style={{ color: 'rgba(255,255,255,0.82)' }}>{cx.hero.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/cx/assessment" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'var(--amber)', color: 'var(--ink)' }}>
                {cx.hero.cta1} <ArrowRight size={18} />
              </Link>
              <a href="#delivery" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.14)', color: 'var(--white)' }}>
                {cx.hero.cta2}
              </a>
            </div>
          </div>
        </section>

        {/* Buyer needs */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <Tag>{cx.needs.tag}</Tag>
          <H2>{cx.needs.title}</H2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {cx.needs.items.map((item, i) => (
              <div key={i} className="p-8 rounded-2xl rev" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
                <IconCircle icon={item.icon} />
                <h3 className="text-xl font-bold mt-6 mb-3" style={display}>{item.title}</h3>
                <p className="leading-relaxed" style={{ color: 'var(--slate)' }}>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Service lines */}
        <section id="services" style={{ background: 'var(--off-white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Tag>{cx.services.tag}</Tag>
            <H2>{cx.services.title}</H2>
            <p className="text-lg max-w-3xl mb-10" style={{ color: 'var(--slate)' }}>{cx.services.subtitle}</p>
            <div className="grid md:grid-cols-2 gap-6">
              {cx.services.items.map((s, i) => (
                <div key={s.slug} className="p-8 rounded-2xl flex flex-col rev" style={{ background: i === 0 ? 'var(--blue-light)' : 'var(--white)', border: '1px solid var(--line)' }}>
                  <div className="flex items-center gap-4 mb-5">
                    <IconCircle icon={s.icon} />
                    <h3 className="text-xl font-bold" style={display}>{s.title}</h3>
                  </div>
                  <p className="leading-relaxed mb-5" style={{ color: 'var(--slate)' }}>{s.summary}</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {s.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: 'var(--white)', color: 'var(--blue)', border: '1px solid var(--line)', ...mono }}>{tag}</span>
                    ))}
                  </div>
                  <Link to={`/cx/services/${CX_SERVICE_SLUGS[i]}`} className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:text-[var(--amber)]" style={{ color: 'var(--blue)' }}>
                    {cx.services.learnMore} <ArrowRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Delivery model */}
        <section id="delivery" className="max-w-6xl mx-auto px-6 py-20">
          <Tag>{cx.delivery.tag}</Tag>
          <H2>{cx.delivery.title}</H2>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {[{ h: cx.delivery.cyprus, Icon: Anchor }, { h: cx.delivery.transylvania, Icon: Map }].map(({ h, Icon }, i) => (
              <div key={i} className="p-8 rounded-2xl rev" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--blue)', color: 'var(--white)' }}><Icon size={20} /></div>
                  <h3 className="text-xl font-bold" style={display}>{h.title}</h3>
                </div>
                <ul className="space-y-3">
                  {h.points.map((p, j) => (
                    <li key={j} className="flex gap-3 leading-relaxed" style={{ color: 'var(--slate)' }}>
                      <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--amber)' }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-8 text-base font-semibold" style={{ color: 'var(--blue)' }}>{cx.delivery.foot}</p>
        </section>

        {/* Why Cyprus */}
        <section style={{ background: 'var(--ink)', color: 'var(--white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Tag light>{cx.cyprus.tag}</Tag>
            <H2 light>{cx.cyprus.title}</H2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10 mb-10">
              {cx.cyprus.stats.map((s, i) => (
                <div key={i} className="p-6 rounded-2xl rev" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--f-display)' }}>{s.value}</div>
                  <div className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.72)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <p className="text-lg leading-relaxed max-w-4xl" style={{ color: 'rgba(255,255,255,0.86)' }}>{cx.cyprus.body}</p>
          </div>
        </section>

        {/* Sectors */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <Tag>{cx.sectors.tag}</Tag>
          <H2>{cx.sectors.title}</H2>
          <div className="mt-10 divide-y" style={{ borderColor: 'var(--line)' }}>
            {cx.sectors.items.map((s, i) => (
              <div key={i} className="grid md:grid-cols-[auto_260px_1fr] gap-4 md:gap-8 items-center py-5 rev" style={{ borderColor: 'var(--line)' }}>
                <IconCircle icon={s.icon} />
                <h3 className="text-lg font-bold" style={display}>{s.title}</h3>
                <p className="leading-relaxed" style={{ color: 'var(--slate)' }}>{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How we operate */}
        <section style={{ background: 'var(--off-white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Tag>{cx.operate.tag}</Tag>
            <H2>{cx.operate.title}</H2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10 mb-12">
              {cx.operate.steps.map((st, i) => (
                <div key={i} className="p-6 rounded-2xl rev" style={{ background: i === 2 ? 'var(--blue-light)' : 'var(--white)', border: '1px solid var(--line)' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'var(--blue)', color: 'var(--white)', ...mono }}>{st.n}</span>
                    <h3 className="text-lg font-bold" style={display}>{st.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--slate)' }}>{st.text}</p>
                </div>
              ))}
            </div>
            <h3 className="text-xl font-bold mb-5" style={display}>{cx.operate.standardsTitle}</h3>
            <ul className="grid md:grid-cols-2 gap-x-10 gap-y-3">
              {cx.operate.standards.map((s, i) => (
                <li key={i} className="flex gap-3 leading-relaxed" style={{ color: 'var(--slate)' }}>
                  <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--amber)' }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Commercial model */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <Tag>{cx.commercial.tag}</Tag>
          <H2>{cx.commercial.title}</H2>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {[cx.commercial.pilot, cx.commercial.pricing].map((col, i) => (
              <div key={i} className="p-8 rounded-2xl rev" style={{ background: i === 0 ? 'var(--blue-light)' : 'var(--white)', border: '1px solid var(--line)' }}>
                <h3 className="text-xl font-bold mb-5" style={display}>{col.title}</h3>
                <ul className="space-y-3">
                  {col.points.map((p, j) => (
                    <li key={j} className="flex gap-3 leading-relaxed" style={{ color: 'var(--slate)' }}>
                      <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--amber)' }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* AI-enabled CX */}
        <section style={{ background: 'var(--off-white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Tag>{cx.ai.tag}</Tag>
            <H2>{cx.ai.title}</H2>
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              {cx.ai.items.map((item, i) => (
                <div key={i} className="p-8 rounded-2xl rev" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
                  <div className="flex items-center gap-4 mb-4">
                    <IconCircle icon={item.icon} />
                    <h3 className="text-lg font-bold" style={display}>{item.title}</h3>
                  </div>
                  <p className="leading-relaxed" style={{ color: 'var(--slate)' }}>{item.text}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-semibold" style={{ color: 'var(--blue)' }}>{cx.ai.foot}</p>
          </div>
        </section>

        {/* Leadership */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <Tag>{cx.leadership.tag}</Tag>
          <H2>{cx.leadership.title}</H2>
          <div className="grid md:grid-cols-[1fr_1fr] gap-6 mt-10">
            <div className="p-8 rounded-2xl rev" style={{ background: 'var(--white)', border: '1px solid var(--line)' }}>
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold shrink-0" style={{ background: 'var(--ink)', color: 'var(--white)', fontFamily: 'var(--f-display)' }}>DD</div>
                <div>
                  <div className="text-xl font-bold" style={display}>{cx.leadership.name}</div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--blue)' }}>{cx.leadership.role}</div>
                </div>
              </div>
              <p className="leading-relaxed mb-5" style={{ color: 'var(--slate)' }}>{cx.leadership.lead}</p>
              <ul className="space-y-2">
                {cx.leadership.facts.map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed" style={{ color: 'var(--ink)' }}>
                    <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--amber)' }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-bold" style={display}>{cx.leadership.meansTitle}</h3>
              {cx.leadership.means.map((m, i) => (
                <div key={i} className="p-5 rounded-2xl flex items-start gap-4 rev" style={{ background: 'var(--off-white)', border: '1px solid var(--line)' }}>
                  <span className="mt-1 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--blue)', color: 'var(--white)', ...mono }}>{i + 1}</span>
                  <p className="leading-relaxed" style={{ color: 'var(--slate)' }}>{m}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Governance */}
        <section style={{ background: 'var(--off-white)' }}>
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Tag>{cx.governance.tag}</Tag>
            <H2>{cx.governance.title}</H2>
            <div className="mt-10 divide-y" style={{ borderColor: 'var(--line)' }}>
              {cx.governance.items.map((g, i) => (
                <div key={i} className="grid md:grid-cols-[auto_220px_1fr] gap-4 md:gap-8 items-center py-5 rev">
                  <IconCircle icon={g.icon} dark />
                  <h3 className="text-lg font-bold" style={display}>{g.title}</h3>
                  <p className="leading-relaxed" style={{ color: 'var(--slate)' }}>{g.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="rounded-2xl p-10 md:p-14" style={{ background: 'var(--blue)', color: 'var(--white)' }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--f-display)' }}>{cx.cta.title}</h2>
            <p className="text-lg mb-8 max-w-3xl" style={{ color: 'rgba(255,255,255,0.82)' }}>{cx.cta.subtitle}</p>
            <div className="flex flex-wrap items-center gap-5">
              <Link to="/cx/assessment" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: 'var(--amber)', color: 'var(--ink)' }}>
                {cx.cta.button} <ArrowRight size={18} />
              </Link>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{cx.cta.secondary}</span>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default CxHubPage;
